import { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface ContactMessageParams {
  subject: string;
  message: string;
  type: 'suggestion' | 'report' | 'general';
  relatedItem?: {
    id: string;
    type: string;
    name: string;
  };
}

export function useContactUs() {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const sendMessage = async ({ subject, message, type, relatedItem }: ContactMessageParams) => {
    if (!user) {
      toast.error('Please sign in to send a message');
      return false;
    }

    try {
      setLoading(true);

      // Create the message document
      const messageData = {
        subject,
        message,
        type,
        ...(relatedItem && { relatedItem }), // Only include relatedItem if it exists
        status: 'pending',
        userId: user.uid,
        userDisplayName: user.displayName || '',
        userEmail: user.email || '',
        createdAt: serverTimestamp()
      };

      // Add the message to contactMessages collection
      const messageRef = await addDoc(collection(db, 'contactMessages'), messageData);

      // Create admin notification
      const notificationData = {
        type: 'NEW_CONTACT_MESSAGE',
        messageId: messageRef.id,
        subject,
        userId: user.uid,
        userDisplayName: user.displayName || '',
        userEmail: user.email || '',
        messageType: type,
        ...(relatedItem && { relatedItem }), // Only include relatedItem if it exists
        createdAt: serverTimestamp(),
        read: false
      };

      await addDoc(collection(db, 'adminNotifications'), notificationData);

      toast.success('Message sent successfully');
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { sendMessage, loading };
} 